/**
 * WorkspaceSwitcher — Sidebar-Top-Variante (Spec §2.1, 48px Slot).
 *
 * Sitzt im Sidebar-Header (h-12 = 48px). Akzeptiert `collapsed`:
 *   - Expanded: Avatar (Initialen) + Workspace-Name + Chevron rechts.
 *   - Collapsed: nur Avatar zentriert, Tooltip rechts mit Name.
 *
 * Click öffnet ein Combobox-Popover mit Workspace-Suche, Liste, "+ Neuer Workspace".
 *
 * Optional: lässt sich von außen via `bus`-Event "workspace-switcher:open"
 * öffnen — wird vom globalen Hotkey ⌘⇧P genutzt.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown, ChevronRight, Plus, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { initials } from "@/lib/utils";
import { cn } from "@/lib/utils";

export interface WorkspaceSwitcherProps {
  collapsed?: boolean;
}

export function WorkspaceSwitcher({ collapsed = false }: WorkspaceSwitcherProps) {
  const { workspaces, activeWorkspace: current, setActiveWorkspace, returnToHub } =
    useWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // ⌘⇧P-Hotkey: globaler Custom-Event öffnet das Popover.
  useEffect(() => {
    function onOpen() {
      setOpen(true);
    }
    window.addEventListener("workspace-switcher:open", onOpen);
    return () => window.removeEventListener("workspace-switcher:open", onOpen);
  }, []);

  const switchWs = (id: string) => {
    setActiveWorkspace(id);
    setOpen(false);
    navigate(`/workspaces/${id}/dashboard`);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter(
      (w) =>
        w.name.toLowerCase().includes(q) || w.handle.toLowerCase().includes(q),
    );
  }, [workspaces, query]);

  const triggerLabel = current?.name || "Workspace wählen";
  const triggerInitials = initials(current?.name) || "?";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={collapsed ? 80 : 700}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={collapsed ? `Workspace: ${triggerLabel}` : "Workspace wechseln"}
                className={cn(
                  "group flex h-9 items-center gap-2 rounded-md text-text-primary",
                  "transition-colors duration-fast ease-out-quint",
                  "hover:bg-bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                  collapsed ? "w-9 justify-center px-0" : "w-full px-2",
                )}
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-600 text-tiny font-semibold text-white shadow-xs">
                  {triggerInitials.toUpperCase()}
                </span>
                {!collapsed && (
                  <>
                    <span className="flex min-w-0 flex-1 flex-col items-start">
                      <span className="w-full truncate text-body-strong leading-tight">
                        {triggerLabel}
                      </span>
                      <span className="truncate text-tiny text-text-tertiary">
                        Workspace
                      </span>
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-tertiary group-hover:text-text-secondary" />
                  </>
                )}
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right" sideOffset={8}>
              {triggerLabel}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        side={collapsed ? "right" : "bottom"}
        align={collapsed ? "start" : "start"}
        sideOffset={collapsed ? 12 : 6}
        className="w-72 p-0"
      >
        {/* Suche */}
        <div className="border-b border-border-default p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Workspace suchen…"
              className="h-8 pl-7 text-small"
            />
          </div>
        </div>

        {/* Liste */}
        <div className="max-h-72 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-small text-text-tertiary">
              Kein Workspace gefunden.
            </div>
          ) : (
            filtered.map((w) => {
              const active = current?.id === w.id;
              return (
                <button
                  type="button"
                  key={w.id}
                  onClick={() => switchWs(w.id)}
                  className={cn(
                    "flex w-full items-center gap-2 px-2 py-1.5 text-left",
                    "transition-colors duration-fast hover:bg-bg-hover focus-visible:outline-none focus-visible:bg-bg-hover",
                  )}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-bg-muted text-tiny font-semibold text-text-secondary">
                    {(initials(w.name) || "?").toUpperCase()}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-body-strong text-text-primary">
                      {w.name}
                    </span>
                    <span className="truncate text-tiny text-text-tertiary">
                      {w.role}
                    </span>
                  </span>
                  {w.isAdmin && (
                    <Badge variant="secondary" className="text-tiny">
                      Admin
                    </Badge>
                  )}
                  {active && <Check className="h-4 w-4 text-brand-600" />}
                </button>
              );
            })
          )}
        </div>

        {/* Footer-Aktionen */}
        <div className="border-t border-border-default p-1">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              returnToHub();
            }}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-body text-text-secondary transition-colors duration-fast hover:bg-bg-hover hover:text-text-primary focus-visible:outline-none focus-visible:bg-bg-hover"
          >
            <Plus className="h-4 w-4" />
            <span className="flex-1">Neuer Workspace</span>
            <ChevronRight className="h-3.5 w-3.5 text-text-tertiary" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
